import React, { useState } from 'react';
import { HelpCircle, Users, Link, Brain, BarChart, FileText, ChevronDown, CheckCircle } from 'lucide-react';

const UserGuide = () => {
  const [openStep, setOpenStep] = useState(1);

  const steps = [
    {
      id: 1,
      icon: Users,
      title: "1. Sınıf ve Şube Tanımlama",
      content: "Sistemi kullanmaya başlamadan önce sol menüdeki 'Sınıf Yönetimi' sekmesine gidin. Okulunuzdaki sınıfları (Örn: 9/A, 10/B) sisteme ekleyin. Dilerseniz her sınıfın içine öğrenci listesini manuel olarak da girebilirsiniz, ancak bu zorunlu değildir; öğrenciler test çözerken numaralarını girdiklerinde otomatik olarak sınıflarına yerleşirler.",
      color: "text-blue-600",
      bg: "bg-blue-50"
    },
    {
      id: 2,
      icon: Link,
      title: "2. Öğrencilere Envanter Gönderme",
      content: "Sol menüden 'Envanterler' sekmesine tıklayın. Uygulamak istediğiniz testi (Örn: Holland, RİBA, Öğrenci Bilgi Formu) seçin. Karşınıza çıkan ekrandaki 'Test Linkini Kopyala' butonuna basarak bu bağlantıyı öğrencilerinize WhatsApp veya sınıf grupları üzerinden gönderin. Öğrenciler uygulamaya girmeden, sadece bu linke tıklayarak formu kendi telefonlarından doldurabilirler. İsterseniz testin QR Kodunu da çıktı alıp sınıflara gönderebilir veya koridorlara asabilirsiniz ya da yine whatsapp gruplarına gönderebilirsiniz.",
      color: "text-emerald-600",
      bg: "bg-emerald-50"
    },
    {
      id: 3,
      icon: FileText,
      title: "3. Bireysel Analiz ve Raporlama",
      content: "Öğrenciler testleri çözdükçe, veriler sol menüdeki 'Öğrenci Listesi' sekmesine düşer. 'Bekliyor' durumundaki testlerin yanındaki 'Görüntüle' butonuna basarak öğrencinin raporunu açın. Raporu inceledikten sonra alt kısımdaki 'Yapay Zeka ile Yorumla' butonuna basarak sistemin öğrenciye özel klinik ve akademik bir değerlendirme yazmasını sağlayın.",
      color: "text-purple-600",
      bg: "bg-purple-50"
    },
    {
      id: 4,
      icon: BarChart,
      title: "4. Kurumsal Analiz (Sınıf ve Okul Haritası)",
      content: "Bireysel raporları onayladıktan sonra sol menüdeki 'Kurumsal Analiz' sekmesine geçin. Buradan test türünü seçtiğinizde; sınıfınızın Sosyometri ağını, RİBA Excel çıktılarını veya genel risk haritasını görebilirsiniz. Sınıf veya okul bazlı 'Kurumsal Yapay Zeka Raporu' oluşturarak okul idaresine veya velilere sunum yapabilirsiniz.",
      color: "text-amber-600",
      bg: "bg-amber-50"
    },
    {
      id: 5,
      icon: Brain,
      title: "5. Kredi ve AI Kullanımı",
      content: "Yapay zeka (AI) destekli rapor oluşturma işlemleri hesabınızdaki kredilerden harcar. Her testin analiz zorluğuna göre bir kredi maliyeti vardır. Kredileriniz bittiğinde 'Ayarlar' sekmesinden Premium (Sınırsız) plana geçiş yapabilirsiniz.",
      color: "text-rose-600",
      bg: "bg-rose-50"
    }
  ];

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto animate-in fade-in zoom-in-95 duration-500">
      <div className="mb-8">
        <h2 className="text-3xl font-black text-slate-800 flex items-center">
          <HelpCircle className="h-8 w-8 mr-3 text-blue-600" />
          Nasıl Kullanılır?
        </h2>
        <p className="text-slate-500 font-medium mt-2">Rehber360 dijital asistanınızı en verimli şekilde kullanmak için aşağıdaki adımları izleyin.</p>
      </div>

      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 md:p-8 bg-slate-50 border-b border-slate-200">
          <h3 className="font-bold text-slate-800 uppercase tracking-widest text-sm flex items-center">
            <CheckCircle className="h-5 w-5 mr-2 text-emerald-500"/>
            Adım Adım Başlangıç Rehberi
          </h3>
        </div>

        <div className="divide-y divide-slate-100">
          {steps.map((step) => (
            <div key={step.id} className="transition-colors">
              <button 
                onClick={() => setOpenStep(openStep === step.id ? null : step.id)}
                className="w-full p-6 flex items-center justify-between hover:bg-slate-50 focus:outline-none transition-colors"
              >
                <div className="flex items-center text-left">
                  <div className={`h-12 w-12 rounded-2xl flex items-center justify-center mr-4 shrink-0 ${step.bg} ${step.color}`}>
                    <step.icon className="h-6 w-6" />
                  </div>
                  <span className="font-bold text-lg text-slate-800">{step.title}</span>
                </div>
                <ChevronDown className={`h-6 w-6 text-slate-400 transition-transform duration-300 ${openStep === step.id ? 'rotate-180' : ''}`} />
              </button>
              
              {openStep === step.id && (
                <div className="px-6 pb-6 pt-2 pl-24 animate-in slide-in-from-top-2 duration-300">
                  <p className="text-slate-600 font-medium leading-relaxed bg-slate-50 p-5 rounded-2xl border border-slate-100">
                    {step.content}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      <div className="mt-8 text-center bg-blue-50 border border-blue-100 rounded-2xl p-6">
        <p className="text-blue-800 font-bold">Daha fazla yardıma mı ihtiyacınız var?</p>
        <p className="text-sm text-blue-600 mt-1">Sol menüdeki Ayarlar sekmesinden destek ekibimizle iletişime geçebilirsiniz.</p>
      </div>
    </div>
  );
};

export default UserGuide;